import React from 'react';
import { AreaCategory, staticMachineAreaZones } from 'fitbeat.gym-core';
import moment from 'moment-timezone';

import HomeIcon from '../assets/icons/home-area-icon.svg';
import CircuitIcon from '../assets/icons/circuit-area-icon.svg';
import TreadmillIcon from '../assets/icons/treadmill-icon.svg';
import BikeIcon from '../assets/icons/bike-area-icon.svg';
import EllipticalIcon from '../assets/icons/elliptical-area-icon.svg';
import PowerIcon from '../assets/icons/power-icon.svg';
import DumbbellIcon from '../assets/icons/dumbbell-area-icon.svg';
import CalisthenicIcon from '../assets/icons/calisthenic-area-icon.svg';
import BenchAndDumbbellIcon from '../assets/icons/bench-and-dumbbell-area-icon.svg';
import BoxingIcon from '../assets/icons/boxing-area-icon.svg';
import StepIcon from '../assets/icons/step-area-icon.svg';
import RowerIcon from '../assets/icons/rower-area-icon.svg';
import workoutSummaryImage from '../assets/images/workout-booking-summary.png';
import homeSummaryImage from '../assets/images/home-summary-image.png';
import circuitSummaryImage from '../assets/images/circuit-summary-image.png';
import treadmillSummaryImage from '../assets/images/treadmill-summary-image.png';
import bicycleSummaryImage from '../assets/images/bicycle-summary-image.png';
import stepSummaryImage from '../assets/images/step-summary-image.jpg';
import ellipticalSummaryImage from '../assets/images/elliptical-summary-image.png';
import rowerSummaryImage from '../assets/images/rower-summary-image.jpg';
import calisthenicSummaryImage from '../assets/images/calisthenic-summary-image.jpg';
import boxingSummaryImage from '../assets/images/boxing-summary-image.jpg';
import benchAndDumbbellSummaryImage from '../assets/images/bench-and-dumbbell-summary-image.jpg';
import dumbbellSummaryImage from '../assets/images/dumbbell-summary-image.jpg';
import powerSummaryImage from '../assets/images/power-summary-image.png';
import Theme from '../theme';
import { TranslateProp } from '../common-types';
import { capitaliseWord } from './utils';

export const getAreaIcon = (areaCategory: AreaCategory | 'home', fill?: string) => {
  const iconFill = fill || Theme.CHARCOAL;

  switch (areaCategory) {
    case 'home':
      return <HomeIcon fill={iconFill} width={30} height={30} />;
    case 'circuit':
      return <CircuitIcon fill={iconFill} width={30} height={30} />;
    case 'treadmill':
      return <TreadmillIcon fill={iconFill} width={30} height={30} />;
    case 'bicycle':
      return <BikeIcon fill={iconFill} width={30} height={30} />;
    case 'elliptical':
      return <EllipticalIcon fill={iconFill} width={30} height={30} />;
    case 'dumbbell':
      return <DumbbellIcon fill={iconFill} width={30} height={30} />;
    case 'benchAndDumbbell':
      return <BenchAndDumbbellIcon fill={iconFill} width={30} height={30} />;
    case 'calisthenic':
      return <CalisthenicIcon fill={iconFill} width={30} height={30} />;
    case 'step':
      return <StepIcon fill={iconFill} width={30} height={30} />;
    case 'rower':
      return <RowerIcon fill={iconFill} width={30} height={30} />;
    case 'boxing':
      return <BoxingIcon fill={iconFill} width={30} height={30} />;
    case 'power':
      return <PowerIcon fill={iconFill} width={30} height={30} />;
    default:
      return undefined;
  }
};

export function getAreaCategorySummaryImage(areaCategory: AreaCategory | 'home') {
  switch (areaCategory) {
    case 'home':
      return homeSummaryImage;
    case 'circuit':
      return circuitSummaryImage;
    case 'treadmill':
      return treadmillSummaryImage;
    case 'bicycle':
      return bicycleSummaryImage;
    case 'step':
      return stepSummaryImage;
    case 'elliptical':
      return ellipticalSummaryImage;
    case 'rower':
      return rowerSummaryImage;
    case 'calisthenic':
      return calisthenicSummaryImage;
    case 'dumbbell':
      return dumbbellSummaryImage;
    case 'benchAndDumbbell':
      return benchAndDumbbellSummaryImage;
    case 'boxing':
      return boxingSummaryImage;
    case 'power':
      return powerSummaryImage;
    default:
      return workoutSummaryImage;
  }
}

export function getAreaCategorySummaryTitle(areaCategory: AreaCategory | 'home', t: TranslateProp): string {
  return t(`${areaCategory}Title`);
}

export function getAreaCategoryDescription(area: AreaCategory, t: TranslateProp): string {
  return t(`${area}AreaDescription`);
}

// todo: pass in t, and use translations to resolve these
export const getAreaDisplayName = (areaCategory: AreaCategory) => {
  switch (areaCategory) {
    case 'bicycle':
      return 'Bike';
    case 'step':
      return 'Stair Climber';
    case 'dumbbell':
    case 'benchAndDumbbell':
      return 'Functional Station';
    case 'power':
      return 'Power Station';
    case 'calisthenic':
      return 'Calisthenics';
    default:
      return capitaliseWord(areaCategory);
  }
};

export const buildSessionTimingArray = (t, index: number, startTime, endTime, areaCategory) => {
  const workoutMinutes = moment(endTime - startTime).format('mm');

  switch (index) {
    case 0:
      return {
        timestamp: startTime.clone().subtract(5, 'minutes').format('HH:mm'),
        description: t('sessionTimingDescription1'),
      };
    case 1:
      return {
        timestamp: startTime.clone().subtract(1, 'minutes').format('HH:mm'),
        description: t('sessionTimingDescription2'),
      };
    case 2:
      if (areaCategory === 'power') {
        return {
          timestamp: startTime.format('HH:mm'),
          description: t('sessionTimingDescription3C', { areaCategory }),
        };
      }
      if (staticMachineAreaZones.includes(areaCategory)) {
        return {
          timestamp: startTime.format('HH:mm'),
          description: t('sessionTimingDescription3A', { areaCategory }),
        };
      }
      return {
        timestamp: startTime.format('HH:mm'),
        description: t('sessionTimingDescription3B'),
      };
    case 3:
      if (['circuit'].includes(areaCategory)) {
        return {
          timestamp: startTime.clone().add(2, 'minutes').format('HH:mm'),
          description: t('sessionTimingDescription4A'),
        };
      }
      if (staticMachineAreaZones.includes(areaCategory)) {
        return {
          timestamp: startTime.clone().add(1, 'minutes').format('HH:mm'),
          description: t('sessionTimingDescription4B', { workoutMinutes: (workoutMinutes - 2).toString() }),
        };
      }
      return {
        timestamp: startTime.clone().add(2, 'minutes').format('HH:mm'),
        description: t('sessionTimingDescription4C'),
      };
    case 4:
      if (staticMachineAreaZones.includes(areaCategory)) {
        return {
          timestamp: endTime.clone().subtract(1, 'minutes').format('HH:mm'),
          description: t('sessionTimingDescription5A'),
        };
      }
      return {
        timestamp: endTime.clone().subtract(2, 'minutes').format('HH:mm'),
        description: t('sessionTimingDescription5B'),
      };
    default:
      return {
        timestamp: endTime.format('HH:mm'),
        description: t('sessionTimingDescription6'),
      };
  }
};
